import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  getAutocompleteValue,
  mountAddressAutocomplete,
  syncAutocompleteValueWhenReady,
} from '../lib/googleMaps';

export interface AddressAutocompleteHandle {
  getValue: () => string;
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (address: string) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
  id?: string;
  name?: string;
}

const MAX_ATTEMPTS = 3;

function wait(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

const AddressAutocomplete = forwardRef<AddressAutocompleteHandle, AddressAutocompleteProps>(
  function AddressAutocomplete(
    {
      value,
      onChange,
      placeholder = '123 Main Street, City, State 12345',
      className = '',
      required = false,
      id,
      name,
    },
    ref
  ) {
    const hostRef = useRef<HTMLDivElement>(null);
    const onChangeRef = useRef(onChange);
    const [status, setStatus] = useState<'loading' | 'ready' | 'fallback'>('loading');
    const [fallbackValue, setFallbackValue] = useState(value);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    onChangeRef.current = onChange;

    useImperativeHandle(ref, () => ({
      getValue: () => {
        if (status === 'fallback') return fallbackValue.trim();
        if (!hostRef.current) return value.trim();
        return getAutocompleteValue(hostRef.current) || value.trim();
      },
    }), [status, fallbackValue, value]);

    useEffect(() => {
      setFallbackValue(value);
    }, [value]);

    useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    let cleanup: (() => void) | undefined;
    let cancelled = false;
    const initialValue = value;

    const mount = async () => {
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
        if (cancelled) return;

        try {
          const dispose = await mountAddressAutocomplete(host, {
            placeholder,
            initialValue,
            onSelect: (address) => {
              onChangeRef.current(address);
              setFallbackValue(address);
            },
            onInputChange: (address) => {
              onChangeRef.current(address);
              setFallbackValue(address);
            },
          });

          if (cancelled) {
            dispose();
            return;
          }

          cleanup = dispose;
          setErrorMessage(null);
          setStatus('ready');
          if (initialValue) {
            await syncAutocompleteValueWhenReady(host, initialValue);
          }
          return;
          } catch (error) {
            if (cancelled) return;

            const message = error instanceof Error ? error.message : 'Unknown error';
            console.warn(`Address autocomplete attempt ${attempt}/${MAX_ATTEMPTS} failed:`, message);

            if (attempt < MAX_ATTEMPTS) {
              host.innerHTML = '';
              await wait(500 * attempt);
              continue;
            }

            setErrorMessage(message);
            setStatus('fallback');
          }
        }
      };

      void mount();

      return () => {
        cancelled = true;
        cleanup?.();
        host.innerHTML = '';
      };
    // Mount once; value is synced in a separate effect after the widget is ready.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [placeholder]);

    useEffect(() => {
      if (status !== 'ready' || !hostRef.current || !value) return;
      void syncAutocompleteValueWhenReady(hostRef.current, value);
    }, [status, value]);

    if (status === 'fallback') {
      return (
        <div>
          <input
            id={id}
            name={name}
            type="text"
            value={fallbackValue}
            onChange={(e) => {
              setFallbackValue(e.target.value);
              onChange(e.target.value);
            }}
            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary ${className}`}
            placeholder={placeholder}
            autoComplete="off"
            required={required}
          />
          <p className="text-xs text-amber-700 mt-1">
            Address suggestions are unavailable — you can still type your address manually.
            {errorMessage ? ` (${errorMessage})` : null}
          </p>
        </div>
      );
    }

    return (
      <div className={className}>
        {name && <input type="hidden" name={name} value={value} />}
        <div
          id={id}
          ref={hostRef}
          className={`address-autocomplete-host w-full ${status === 'loading' ? 'min-h-[42px] rounded-lg border border-gray-300 bg-white' : ''}`}
          aria-required={required}
        />
        {status === 'loading' && (
          <p className="sr-only">Loading address suggestions…</p>
        )}
      </div>
    );
  }
);

export default AddressAutocomplete;
